/**
 * Debug surface exposed on `window.__game`.
 *
 * This exists so an automated harness can prove the simulation actually ran,
 * rather than only proving the bundle compiled. `tests/verify.mjs` drives it.
 */

import type { AssetReport } from '../assets/GameAssets';

export interface GameSnapshot {
  state: string;
  /** Ball centre, world space. */
  ballX: number;
  ballY: number;
  ballZ: number;
  /** Ball linear speed, m/s. */
  ballSpeed: number;
  /** Radial distance of the ball from the board centre. */
  ballRadius: number;
  /** Board rotation, UNBOUNDED - may exceed +/-360. */
  angleDeg: number;
  /** Current board angular velocity, rad/s. */
  omega: number;
  spawned: boolean;
  /**
   * True while Rapier has the ball asleep.
   *
   * Worth exposing: a sleeping body is skipped by the solver AND by gravity, so
   * a ball that dozes off part-way up a channel stays there instead of rolling
   * to the bottom. That is a physics-configuration bug, not a gameplay one, and
   * it is invisible from position alone.
   */
  ballSleeping: boolean;
  bodyCount: number;
  colliderCount: number;
  /**
   * Number of cuboid wall segments `buildMazeLayout()` produced.
   *
   * Exposed so the harness can tie the live physics world back to the layout
   * table: the world's collider count must be exactly the plate, the cover, the
   * hub, the ball, and one cuboid per wall segment. A collider that appears
   * without a matching layout entry - an invisible wall - fails that sum.
   */
  wallSegments: number;
  /** Rendered frames since boot. */
  frames: number;

  // ---- scoring -------------------------------------------------------------
  /** 1-based round counter, incremented by every restart. */
  round: number;
  /** Points awarded in the current round; 0 while the round is still live. */
  score: number;
  /** Points across every round played since boot. */
  totalScore: number;
  /** Resolved pocket id, or null while the round is live / after a miss. */
  slotId: string | null;
  /** Resolved pocket label, or null. */
  slotLabel: string | null;
  /** `none` while live, then `scored` or `lost`. */
  outcome: string;
  /** True once the round's result is final - no further scoring is possible. */
  resolved: boolean;
  /** Consecutive fixed steps the ball has been below the settle speed. */
  restSteps: number;
  /**
   * How many times the current round has awarded points.
   *
   * Exposed specifically so the harness can prove the ball is never paid twice
   * for one descent: it must be 0 while live and exactly 1 afterwards.
   */
  scoreEvents: number;

  // ---- the round's clock ---------------------------------------------------
  /** Whole seconds left on the 3-2-1 countdown; 0 once it has finished. */
  countdownRemaining: number;
  /** True while the countdown is holding the ball above the board. */
  held: boolean;
  /** Simulated seconds since the ball was released. 0 before release. */
  roundElapsed: number;
  /** True once the ball's centre has crossed the scoring row's mouth. */
  hasEnteredRow: boolean;

  // ---- the machine ---------------------------------------------------------
  /** Colliders belonging to the scoring row's fixed body. */
  scoreRowColliders: number;
  /** Peg/obstacle colliders the layout declares, separate from `wallSegments`. */
  layoutObstacles: number;
  /** The scoring row's derived geometry, so the harness can check the row it got. */
  scoreRow: {
    count: number;
    mouthY: number;
    floorY: number;
    span: number;
    totalWidth: number;
  };
  /** True when the cabinet shell is in the scene rather than missing. */
  cabinetVisible: boolean;

  // ---- arcade loop (P1) ---------------------------------------------------
  /** Balls remaining this run (GAME.balls); a miss costs one. */
  ballsLeft: number;
  /** Best total across sessions, read from localStorage. */
  best: number;
}

export interface PixelSample {
  width: number;
  height: number;
  sampled: number;
  /** Distinct colours after quantising each channel to 32 levels. */
  distinctColors: number;
  /** Fraction of pixels that are NOT the single most common colour. */
  nonDominantFraction: number;
  /** Mean luminance in [0, 1]. */
  meanLuminance: number;
  /** True when the frame clearly contains rendered content rather than a flat fill. */
  hasContent: boolean;
}

/**
 * A world-space axis-aligned bounding box, flattened to arrays so it survives
 * `page.evaluate()`.
 */
export interface BoundsSnapshot {
  min: [number, number, number];
  max: [number, number, number];
  center: [number, number, number];
  size: [number, number, number];
}

/**
 * The radial band a node's vertices occupy, measured from the board's rotation
 * axis.
 *
 * A world-axis bounding box cannot express this: a ring is a circular arc, so
 * its box half-width is `radius + thickness / 2` only when the arc happens to
 * cover both axis extremes, and it overstates the inner surface by a factor of
 * `sqrt(2)` at the corners. `min` and `max` here are the two numbers that
 * actually have to agree with the collider - the surfaces the ball touches.
 */
export interface RadialExtentSnapshot {
  /** Closest vertex distance from the board axis. */
  min: number;
  /** Furthest vertex distance from the board axis. */
  max: number;
}

export interface GameDebugApi {
  snapshot(): GameSnapshot;
  /** Set the board's target angular velocity, rad/s. Used by the harness. */
  setOmega(omega: number): void;
  /**
   * Jump the board to an absolute angle, in degrees.
   *
   * The board is kinematic, so this is a legitimate way to pose it - it is what
   * the player's rotation ultimately produces. The harness uses it to line a
   * specific ring gap up with the top of the board, where gravity carries the
   * ball inward, instead of guessing how far a given `omega` will overshoot.
   */
  setBoardAngle(deg: number): void;
  /** Restart the round: respawn the ball and clear the round's scoring state. */
  reset(): void;
  /** Restart the round AND zero the running total. */
  resetAll(): void;
  spawn(): void;
  /**
   * End the countdown immediately and let the ball go.
   *
   * Exists so a test can skip the three seconds of aiming window without having
   * to wait for them in simulated time. It is a shortcut for a transition the
   * player can cause by waiting, not a separate code path.
   */
  releaseBall(): void;
  /** Ask the render loop to sample the framebuffer on its next frame. */
  requestPixelSample(): void;
  /** Result of the most recent sample, or null if none has been taken yet. */
  pixelSample(): PixelSample | null;

  // ---- asset integration ---------------------------------------------------
  /**
   * Which production models actually loaded, and whether any fell back to
   * procedural geometry. This is what stops a silent substitution from looking
   * like a successful integration.
   */
  assetReport(): AssetReport;
  /** Mesh node names currently under the board's visual root. */
  boardNodeNames(): string[];
  /** Mesh node names currently under the cabinet's visual root. */
  cabinetNodeNames(): string[];
  /** Mesh node names currently under the scoring row's visual root. */
  scoreRowNodeNames(): string[];
  /** The text currently shown on the marquee, as three readouts. */
  displayText(): { timer: string; round: string; total: string };
  /** Live state of the input layer the on-screen controls can assert on. */
  controlState(): {
    coarsePointer: boolean;
    gyroSupported: boolean;
    gyroEnabled: boolean;
    gyroNeutralDeg: number | null;
  };
  /** World-space bounds of the whole board visual. */
  boardBounds(): BoundsSnapshot;
  /** World-space bounds of the whole cabinet visual, or null if it did not load. */
  cabinetBounds(): BoundsSnapshot | null;
  /** World-space bounds of the first node with this name, or null if absent. */
  nodeBounds(name: string): BoundsSnapshot | null;
  /**
   * Radial band occupied by the first node with this name, or null if absent.
   *
   * This is the measurement the alignment assertions use: a ring wall's `min`
   * must equal its collider's inner face and its `max` the collider's outer
   * face.
   */
  nodeRadialExtent(name: string): RadialExtentSnapshot | null;
  /** Place the ball at an explicit world position, at rest and released. */
  teleportBall(x: number, y: number, z: number): void;
  /**
   * Which pocket the row resolves an x to, or null if it is outside the row.
   *
   * The scoring rule itself, callable without driving a ball anywhere. It is what
   * lets the harness assert the pocket boundaries directly - "x = 0 is the bonus,
   * x = 1.0 is the outermost" - rather than inferring them from landings.
   */
  slotAt(x: number): { id: string; label: string; points: number; tier: number } | null;
  /**
   * Show or hide the top-down collision view.
   *
   * Exposed because the view is what makes a too-narrow passage visible, and a
   * probe that reports "the ball never got through" needs a way to show why.
   */
  setDebugView(visible: boolean): void;
  /** What the top-down view drew on its last frame. */
  debugViewStats(): { segments: number; obstacles: number; pockets: number; ballDrawn: boolean };
  /**
   * Project a world point to CSS pixels, or null if it is behind the camera.
   *
   * Exists so a probe can sample a KNOWN surface rather than hunting for it in a
   * screenshot: "is the bonus pocket actually violet" is only a real question if
   * the pixel sampled is the pocket's own back panel and not the divider next to
   * it. Returns CSS pixels with the origin at the canvas's top-left, matching
   * `page.screenshot()` and `elementHandle.boundingBox()`.
   */
  projectPoint(x: number, y: number, z: number): { x: number; y: number; visible: boolean } | null;
  /**
   * Every pocket's centre, in world space and on screen.
   *
   * The world point is the centre of the pocket's BACK PANEL - the surface the
   * player sees straight on - not the mouth, which is in mid-air. Ordered left to
   * right, exactly like `slotsLeftToRight()`.
   */
  slotAnchors(): {
    id: string;
    tier: number;
    points: number;
    x: number;
    y: number;
    z: number;
    screenX: number;
    screenY: number;
    visible: boolean;
  }[];
}

declare global {
  interface Window {
    __game?: GameDebugApi;
  }
}

export function installDebugApi(api: GameDebugApi): void {
  window.__game = api;
}

/**
 * Quantise the current framebuffer into coarse statistics.
 *
 * Reads the drawing buffer directly rather than going through a 2D canvas,
 * because a WebGL canvas is only readable without `preserveDrawingBuffer` while
 * still inside the frame that drew it.
 */
export function samplePixels(
  gl: WebGLRenderingContext | WebGL2RenderingContext,
  width: number,
  height: number,
): PixelSample {
  const buffer = new Uint8Array(width * height * 4);
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, buffer);

  const counts = new Map<number, number>();
  let luminanceSum = 0;
  let sampled = 0;

  // Step across the buffer rather than reading every pixel of a large canvas.
  const stride = 4;
  for (let i = 0; i < buffer.length; i += 4 * stride) {
    const r = buffer[i] ?? 0;
    const g = buffer[i + 1] ?? 0;
    const b = buffer[i + 2] ?? 0;
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    counts.set(key, (counts.get(key) ?? 0) + 1);
    luminanceSum += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    sampled += 1;
  }

  let dominant = 0;
  for (const count of counts.values()) {
    if (count > dominant) dominant = count;
  }

  const nonDominantFraction = sampled > 0 ? 1 - dominant / sampled : 0;
  const meanLuminance = sampled > 0 ? luminanceSum / sampled : 0;

  return {
    width,
    height,
    sampled,
    distinctColors: counts.size,
    nonDominantFraction,
    meanLuminance,
    // A flat fill produces one or two quantised colours; a rendered scene does not.
    hasContent: counts.size >= 24 && nonDominantFraction > 0.25 && meanLuminance > 0.01,
  };
}
